// javascript
import { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import WarningIcon from '@mui/icons-material/Warning';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PercentIcon from '@mui/icons-material/Percent';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import BusinessIcon from '@mui/icons-material/Business';
import DescriptionIcon from '@mui/icons-material/Description';
import cambioEstadoService from '../services/cambioEstadoService';
import { subirArchivo } from '../services/archivoService';
import DatosMaestrosService from '../services/datosMaestrosService';
import fechasUtils from '../utils/fechasUtils';
import ComboboxDatosMaestros from '../components/ComboboxDatosMaestros';
import ModalConfirmacionCambioEstado from '../components/ModalConfirmacionCambioEstado';
import EventoService from '../services/eventoService';

// Función auxiliar para obtener la fecha actual en formato YYYY-MM-DD
const obtenerFechaActual = () => {
    return fechasUtils.formatDateISO(new Date());
};

function GestorEstadoSegundaApelacion({ tramite, onEstadoCambiado }) {
    const [cargando, setCargando] = useState(false);
    const [mostrarFormularioApelacion, setMostrarFormularioApelacion] = useState(false);
    // const [historialEstados, setHistorialEstados] = useState([]); // Eliminado
    const [cargandoHistorial, setCargandoHistorial] = useState(true);
    const [infoDictamenCMC, setInfoDictamenCMC] = useState(null);
    const [diasRestantes, setDiasRestantes] = useState(null);
    const [colorAlerta, setColorAlerta] = useState('secondary');
    const [vencido, setVencido] = useState(false);
    const [mostrarModalCerrarTramite, setMostrarModalCerrarTramite] = useState(false);

    // Estados del formulario
    const [datosApelacion, setDatosApelacion] = useState({
        fechaNotificacion: obtenerFechaActual(),
        porcentaje: '',
        fechaLimite: '',
        numeroExpedienteJudicial: '',
        juzgado: '',
        sala: '',
        fechaPresentacion: obtenerFechaActual(),
        archivoDemanda: null
    });
    const [archivos, setArchivos] = useState([]);
    const [lugares, setLugares] = useState([]);
    const [feriados, setFeriados] = useState([]);
    const [subiendoArchivos, setSubiendoArchivos] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    useEffect(() => {
        const cargarDatos = async () => {
            // Cargar feriados primero
            try {
                const feriadosData = await fechasUtils.obtenerFeriadosActivos();
                setFeriados(feriadosData);
            } catch (error) {
                console.error('Error al cargar feriados:', error);
            }

            cargarHistorial();
            cargarLugares();
            // Calcular fecha límite inicial si hay fecha de notificación
            if (datosApelacion.fechaNotificacion && !datosApelacion.fechaLimite) {
                handleFechaNotificacionChange(datosApelacion.fechaNotificacion);
            }
        };

        cargarDatos();
    }, [tramite.idTramite]);

    const cargarLugares = async () => {
        try {
            const response = await DatosMaestrosService.getLugares();
            setLugares(response.data);
        } catch (error) {
            console.error('Error al cargar lugares:', error);
        }
    };

    useEffect(() => {
        if (infoDictamenCMC && infoDictamenCMC.fechaLimite) {
            calcularDiasRestantes();
            const interval = setInterval(() => {
                calcularDiasRestantes();
            }, 60000); // Actualizar cada minuto

            return () => clearInterval(interval);
        }
    }, [infoDictamenCMC]);

    // Efecto para mostrar notificaciones cuando el plazo está por vencer
    useEffect(() => {
        if (diasRestantes !== null && !vencido && diasRestantes <= 2 && diasRestantes >= 0) {
            const timeoutId = setTimeout(() => {
                if (diasRestantes === 2) {
                    toast.warning(`⚠️ Alerta: El trámite tiene 2 días restantes para presentar la segunda apelación. Fecha límite: ${infoDictamenCMC?.fechaLimite || 'N/A'}`, {
                        autoClose: 10000
                    });
                } else if (diasRestantes === 1) {
                    toast.error(`🚨 URGENTE: El trámite tiene 1 día restante para presentar la segunda apelación. Fecha límite: ${infoDictamenCMC?.fechaLimite || 'N/A'}`, {
                        autoClose: 15000
                    });
                } else if (diasRestantes === 0) {
                    toast.error(`🔴 CRÍTICO: ¡ÚLTIMO DÍA! El plazo para presentar la segunda apelación vence hoy: ${infoDictamenCMC?.fechaLimite || 'N/A'}`, {
                        autoClose: 20000
                    });
                }
            }, 1000);

            return () => clearTimeout(timeoutId);
        }
    }, [diasRestantes, vencido]);

    const cargarHistorial = async () => {
        setCargandoHistorial(true);
        try {
            const historial = await cambioEstadoService.getHistorialEstados(tramite.idTramite);
            // setHistorialEstados(historial); // Eliminado

            // Buscar el cambio de estado que contiene el dictamen CMC (estado 15 -> 10)
            const cambioDictamenCMC = historial
                .filter(c => c.comentario && c.comentario.includes('Dictamen CMC recibido'))
                .pop();

            if (cambioDictamenCMC && cambioDictamenCMC.comentario) {
                parsearInfoDictamenCMC(cambioDictamenCMC.comentario);
            }
        } catch (error) {
            console.error("Error al cargar historial:", error);
            toast.error("No se pudo cargar la información del dictamen CMC.");
        } finally {
            setCargandoHistorial(false);
        }
    };

    const sincronizarEventoVencimiento = async (fechaDate, tipoApelacion) => {
        if (!fechaDate || !tramite.idTramite) return;

        try {
            // Formatear fecha para el evento
            const fechaNotificacionStr = fechasUtils.formatDateISO(fechaDate); // YYYY-MM-DD
            const tituloEvento = `Vencimiento ${tipoApelacion}`;
            const descripcion = `Vencimiento automático calculado para la ${tipoApelacion} del trámite #${tramite.idTramite}`;
            // Recordatorios: 2 días, 1 día, 2 horas antes
            const tiemposRecordatorio = [48, 24, 2];

            // Obtener eventos existentes del trámite usando método correcto
            const respuesta = await EventoService.getEventosPorTramite(tramite.idTramite);
            const eventos = respuesta.data || [];

            // Buscar si ya existe este evento específico
            const eventoExistente = eventos.find(e =>
                e.titulo === tituloEvento &&
                (e.tipoEvento === 'Vencimiento' || e.tipo === 'Vencimiento')
            );

            if (eventoExistente) {
                // Verificar si la fecha coincide
                const fechaExistente = eventoExistente.fecha;

                if (fechaExistente !== fechaNotificacionStr) {
                    console.log(`Actualizando evento de vencimiento: ${fechaExistente} -> ${fechaNotificacionStr}`);
                    await EventoService.modificarEvento(eventoExistente.id || eventoExistente.idEvento, {
                        ...eventoExistente,
                        fecha: fechaNotificacionStr,
                        horaInicio: '08:00',
                        horaFin: '14:00',
                        tiemposRecordatorio
                    });
                     toast.info(`📅 Fecha de vencimiento actualizada en el calendario: ${fechaNotificacionStr}`);
                }
            } else {
                // Crear nuevo evento
                console.log("Creando nuevo evento de vencimiento en calendario");
                await EventoService.crearEvento({
                    titulo: tituloEvento,
                    descripcion: descripcion,
                    fecha: fechaNotificacionStr,
                    horaInicio: '08:00',
                    horaFin: '14:00',
                    todoElDia: false,
                    tipoEvento: 'Vencimiento',
                    idTramite: tramite.idTramite,
                    visibilidad: 'POR_AREA',
                    canalesNotificacion: {
                        whatsapp: false,
                        email: false,
                        inapp: true
                    },
                    tiemposRecordatorio
                });
                toast.success(`📅 Evento de vencimiento creado en el calendario para el ${fechaNotificacionStr}`);
            }

        } catch (error) {
            console.error("Error al sincronizar evento de vencimiento:", error);
        }
    };

    const eliminarEventoVencimiento = async (tipoApelacion) => {
        try {
            const tituloEvento = `Vencimiento ${tipoApelacion}`;
            const respuesta = await EventoService.getEventosPorTramite(tramite.idTramite);
            const eventos = respuesta.data || [];

            const eventoExistente = eventos.find(e =>
                e.titulo === tituloEvento &&
                (e.tipoEvento === 'Vencimiento' || e.tipo === 'Vencimiento')
            );

            if (eventoExistente) {
                await EventoService.eliminarEvento(eventoExistente.id || eventoExistente.idEvento);
                console.log(`🗑️ Evento de vencimiento eliminado automáticamente: ${tituloEvento}`);
            }
        } catch (error) {
            console.error("Error al eliminar evento de vencimiento:", error);
        }
    };

    const parsearInfoDictamenCMC = async (comentario) => {
        const info = {
            porcentaje: null,
            fechaDictamen: null,
            resultado: null,
            fechaLimite: null
        };

        // Extraer porcentaje
        const porcentajeMatch = comentario.match(/Porcentaje de incapacidad: ([\d.]+)%/);
        if (porcentajeMatch) {
            info.porcentaje = parseFloat(porcentajeMatch[1]);
        }

        // Extraer fecha del dictamen
        const fechaDictamenMatch = comentario.match(/Fecha del dictamen: (.+?)(?:\n|$)/);
        if (fechaDictamenMatch) {
            info.fechaDictamen = fechaDictamenMatch[1].trim();
        }

        // Extraer resultado
        const resultadoMatch = comentario.match(/Resultado: (.+?)(?:\n|$)/);
        if (resultadoMatch) {
            info.resultado = resultadoMatch[1].trim();
        }

        // RECALCULO OBLIGATORIO DE FECHA LÍMITE
        if (info.porcentaje !== null && info.porcentaje < 66 && info.fechaDictamen) {
            console.log("Intentando calcular fecha límite para Segunda Apelación. Dictamen:", info.fechaDictamen);

            // Intentamos parsear la fecha usando la función existente en el ámbito (si existe) o nuestra lógica in-line
            let fechaNotificacion = parsearFechaDesdeTexto(info.fechaDictamen);

            // Si fechaNotificacion es null, intentamos un parseo más agresivo si es formato largo español
            if (!fechaNotificacion && info.fechaDictamen.includes('de')) {
                 // Lógica simple para "10 de febrero de 2026"
                 try {
                     const partes = info.fechaDictamen.toLowerCase().replace(/,/g, '').split(' ');
                     // Buscar num dia
                     const diaIdx = partes.findIndex(p => /^\d{1,2}$/.test(p));
                     const añoIdx = partes.findIndex(p => /^\d{4}$/.test(p));
                     const mesMap = {
                        'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3, 'mayo': 4, 'junio': 5,
                        'julio': 6, 'agosto': 7, 'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
                     };
                     const mesIdx = partes.findIndex(p => mesMap[p] !== undefined);

                     if (diaIdx !== -1 && mesIdx !== -1 && añoIdx !== -1) {
                         fechaNotificacion = new Date(parseInt(partes[añoIdx]), mesMap[partes[mesIdx]], parseInt(partes[diaIdx]));
                     }
                 } catch (e) { console.error("Error parsing manual fecha", e); }
            }

            if (fechaNotificacion) {
                 try {
                    console.log("Fecha notificación parseada:", fechaNotificacion);
                    // Forzamos carga feriados
                    const feriadosActivos = await fechasUtils.obtenerFeriadosActivos();
                    const fechaLimite = await fechasUtils.calcularDiasHabiles(fechaNotificacion, 5, feriadosActivos);

                    // Usar formato largo para visualización amigable
                    info.fechaLimite = fechasUtils.formatDateLong(fechaLimite);
                    console.log("Fecha límite calculada exitosamente:", info.fechaLimite);

                    // Sincronizar con calendario
                    sincronizarEventoVencimiento(fechaLimite, "2° Apelación");

                 } catch (e) {
                     console.error("Error recalculando fecha limite (API):", e);
                     // Fallback local básico
                     const limiteEstimado = new Date(fechaNotificacion);
                     limiteEstimado.setDate(limiteEstimado.getDate() + 7); // ~5 días hábiles
                     const limiteEstimadoStr = fechasUtils.formatDateLong(limiteEstimado);
                     info.fechaLimite = limiteEstimadoStr;
                 }
            } else {
                console.warn("No se pudo parsear la fecha del dictamen:", info.fechaDictamen);
                // Intentar poner una fecha límite vacía o mensaje de error en lugar de dejarla null si es crítico
                // info.fechaLimite = "Error de cálculo";
            }
        } else if (info.porcentaje !== null && info.porcentaje >= 66) {
            // Si el porcentaje es >= 66%, esto no debería pasar (el trámite debería completarse)
            console.warn("⚠️ ADVERTENCIA: El trámite está en Segunda Apelación pero el dictamen CMC otorga >= 66%.");
        }

        setInfoDictamenCMC(info);

        // Pre-llenar formulario si está disponible y el porcentaje es < 66%
        if (info.fechaDictamen && info.porcentaje !== null && info.porcentaje < 66) {
            const fechaNotificacionInput = convertirFechaTextoADateInput(info.fechaDictamen);
            let fechaLimiteInput = '';

            if (info.fechaLimite) {
                fechaLimiteInput = info.fechaLimite;
            }

            setDatosApelacion(prev => ({
                ...prev,
                fechaNotificacion: fechaNotificacionInput,
                porcentaje: info.porcentaje.toString(),
                fechaLimite: fechaLimiteInput
            }));
        }
    };

    const convertirFechaTextoADateInput = (fechaTexto) => {
        try {
            const fechaISO = new Date(fechaTexto);
            if (!isNaN(fechaISO.getTime())) {
                return fechasUtils.formatDateISO(fechaISO);
            }

            const partes = fechaTexto.toLowerCase().split(' ');
            if (partes.length >= 3) {
                const meses = {
                    'enero': '01', 'febrero': '02', 'marzo': '03', 'abril': '04',
                    'mayo': '05', 'junio': '06', 'julio': '07', 'agosto': '08',
                    'septiembre': '09', 'octubre': '10', 'noviembre': '11', 'diciembre': '12'
                };

                let dia = null;
                let mes = null;
                let año = null;

                for (let i = 0; i < partes.length; i++) {
                    const parte = partes[i].replace(',', '').replace('de', '').trim();

                    if (!dia && /^\d{1,2}$/.test(parte)) {
                        const num = parseInt(parte);
                        if (num >= 1 && num <= 31) {
                            dia = num.toString().padStart(2, '0');
                        }
                    }

                    if (!mes && meses[parte]) {
                        mes = meses[parte];
                    }

                    if (!año && /^\d{4}$/.test(parte)) {
                        año = parte;
                    }
                }

                if (dia && mes && año) {
                    return `${año}-${mes}-${dia}`;
                }
            }
        } catch (e) {
            console.error("Error al convertir fecha:", e);
        }
        return '';
    };

    const parsearFechaDesdeTexto = (fechaTexto) => {
        try {
            // Intentar parsear como fecha ISO primero
            const fechaISO = new Date(fechaTexto);
            if (!isNaN(fechaISO.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(fechaTexto)) {
                return fechaISO;
            }

            const meses = {
                'enero': 0, 'febrero': 1, 'marzo': 2, 'abril': 3,
                'mayo': 4, 'junio': 5, 'julio': 6, 'agosto': 7,
                'septiembre': 8, 'octubre': 9, 'noviembre': 10, 'diciembre': 11
            };

            const partes = fechaTexto.toLowerCase().replace(/,/g, '').split(' ');

            let dia = null;
            let mes = null;
            let año = null;

            for (let i = 0; i < partes.length; i++) {
                const parte = partes[i];

                // Buscar día (número entre 1 y 31)
                if (dia === null && /^\d{1,2}$/.test(parte)) {
                    const num = parseInt(parte);
                    if (num >= 1 && num <= 31) {
                        dia = num;
                    }
                }

                // Buscar mes
                if (mes === null && meses[parte] !== undefined) {
                    mes = meses[parte];
                }

                // Buscar año (4 dígitos)
                if (año === null && /^\d{4}$/.test(parte)) {
                    año = parseInt(parte);
                }
            }

            if (dia !== null && mes !== null && año !== null) {
                return new Date(año, mes, dia);
            }

            // Último intento con Date directo si no es ISO pero el navegador lo entiende
            if (!isNaN(fechaISO.getTime())) {
                return fechaISO;
            }

        } catch (e) {
            console.error("Error al parsear fecha:", e);
        }
        return null;
    };

    // Función helper para calcular fecha límite con feriados
    const calcularFechaLimiteConFeriados = async (fechaNotif, dias = 5) => {
        if (!fechaNotif) return null;
        const fecha = new Date(fechaNotif + 'T00:00:00');
        if (isNaN(fecha.getTime())) return null;

        try {
            const fechaLimite = await fechasUtils.calcularDiasHabiles(fecha, dias, feriados.length > 0 ? feriados : null);
            return fechaLimite;
        } catch (error) {
            console.error('Error al calcular fecha límite:', error);
            return null;
        }
    };

    // Función síncrona para calcular días hábiles (usa feriados si están cargados)
    const calcularDiasHabiles = (fechaInicio, dias) => {
        if (feriados.length > 0) {
            // Usar la versión síncrona con feriados
            return fechasUtils.calcularDiasHabilesSync(fechaInicio, dias, feriados);
        } else {
            // Fallback: solo fines de semana si no hay feriados cargados
            let fecha = new Date(fechaInicio);
            fecha.setHours(0, 0, 0, 0);

            let diasAgregados = 0;
            while (diasAgregados < dias) {
                fecha.setDate(fecha.getDate() + 1);
                if (fecha.getDay() !== 0 && fecha.getDay() !== 6) {
                    diasAgregados++;
                }
            }
            return fecha;
        }
    };

    const calcularDiasRestantes = () => {
        if (!infoDictamenCMC || !infoDictamenCMC.fechaLimite) return;

        try {
            const fechaLimiteTexto = infoDictamenCMC.fechaLimite;
            let fechaLimiteDate = parsearFechaDesdeTexto(fechaLimiteTexto);

            if (!fechaLimiteDate && infoDictamenCMC.fechaDictamen) {
                const fechaNot = parsearFechaDesdeTexto(infoDictamenCMC.fechaDictamen);
                if (fechaNot) {
                    if (feriados.length > 0) {
                        fechaLimiteDate = fechasUtils.calcularDiasHabilesSync(fechaNot, 5, feriados);
                    } else {
                        // Fallback local
                        fechaLimiteDate = new Date(fechaNot);
                        fechaLimiteDate.setHours(0, 0, 0, 0);

                        let diasAgregados = 0;
                        while (diasAgregados < 5) {
                            fechaLimiteDate.setDate(fechaLimiteDate.getDate() + 1);
                            if (fechaLimiteDate.getDay() !== 0 && fechaLimiteDate.getDay() !== 6) {
                                diasAgregados++;
                            }
                        }
                    }
                }
            }

            if (!fechaLimiteDate) return;

            const hoy = new Date();
            hoy.setHours(0, 0, 0, 0);
            fechaLimiteDate.setHours(0, 0, 0, 0);

            if (hoy > fechaLimiteDate) {
                 const diffTime = fechaLimiteDate - hoy;
                 const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                 actualizarEstadoContador(diffDays);
                 return;
            }

            // Calcular días hábiles restantes (excluyendo hoy si ya es hoy, o contando desde mañana)
            // Lógica unificada: contar desde mañana hasta fecha límite.
            const manana = new Date(hoy);
            manana.setDate(manana.getDate() + 1);

            const listaFeriados = (feriados && Array.isArray(feriados)) ? feriados : [];
            const diasHabilesRestantes = fechasUtils.contarDiasHabilesSync(manana, fechaLimiteDate, listaFeriados);

            actualizarEstadoContador(diasHabilesRestantes);

        } catch (error) {
            console.error("Error al calcular días restantes:", error);
        }
    };

    const actualizarEstadoContador = (dias) => {
        setDiasRestantes(dias);

        if (dias < 0) {
            setVencido(true);
            setColorAlerta('secondary');
            verificarCierreAutomatico();
        } else if (dias === 0) {
            setVencido(false);
            setColorAlerta('danger');
        } else if (dias <= 2) {
            setVencido(false);
            setColorAlerta('warning');
        } else {
            setVencido(false);
            setColorAlerta('warning');
        }
    };

    const verificarCierreAutomatico = async () => {
        if (tramite.idEstadoActual === 13 || tramite.idEstadoActual === 14) {
            return;
        }
        toast.warning("⚠️ El plazo para presentar la segunda apelación ha vencido.");
    };

    const handleSeleccionarArchivos = (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length === 0) return;

        const nuevosArchivos = files.map(file => ({
            id: `local_${Date.now()}_${Math.random()}`,
            file,
            nombre: file.name,
            tamaño: (file.size / (1024 * 1024)).toFixed(2) + " MB",
            estado: "pendiente",
            esDelBackend: false
        }));

        setArchivos(prev => [...prev, ...nuevosArchivos]);
        if (event.target) event.target.value = "";
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleSeleccionarArchivos({ target: { files: e.dataTransfer.files } });
        }
    };

    const handleEliminarArchivo = (id) => {
        setArchivos(prev => prev.filter(a => a.id !== id));
    };

    const handleFechaNotificacionChange = async (fecha) => {
        const nuevaFecha = fecha;

        // Actualizar la fecha de notificación inmediatamente
        setDatosApelacion(prev => ({
            ...prev,
            fechaNotificacion: nuevaFecha,
            fechaLimite: '' // Limpiar mientras se calcula
        }));

        if (nuevaFecha) {
            // Asegurarse de que la fecha esté en formato Date válido
            const fechaDate = new Date(nuevaFecha + 'T00:00:00');
            if (!isNaN(fechaDate.getTime())) {
                try {
                    // Calcular fecha límite con feriados
                    const fechaLimiteCalculada = await fechasUtils.calcularDiasHabiles(fechaDate, 5, feriados.length > 0 ? feriados : null);
                    const fechaLimite = fechaLimiteCalculada.toISOString().split('T')[0];
                    console.log('📅 Fecha límite recalculada (con feriados):', fechaLimite, 'desde fecha de notificación:', nuevaFecha);

                    // Actualizar con la fecha límite calculada
                    setDatosApelacion(prev => ({
                        ...prev,
                        fechaLimite: fechaLimite
                    }));
                } catch (error) {
                    console.error('Error al calcular fecha límite:', error);
                    // Fallback a cálculo sin feriados si hay error
                    const fechaLimiteCalculada = calcularDiasHabiles(fechaDate, 5);
                    setDatosApelacion(prev => ({
                        ...prev,
                        fechaLimite: fechaLimiteCalculada.toISOString().split('T')[0]
                    }));
                }
            }
        }
    };

    const handleRegistrarSegundaApelacion = async () => {
        // Validaciones
        if (!datosApelacion.fechaNotificacion) {
            toast.error("La fecha de notificación del dictamen CMC es obligatoria.");
            return;
        }
        if (!datosApelacion.porcentaje || datosApelacion.porcentaje === '') {
            toast.error("El porcentaje de incapacidad es obligatorio.");
            return;
        }
        if (!datosApelacion.numeroExpedienteJudicial || datosApelacion.numeroExpedienteJudicial.trim() === '') {
            toast.error("El número de expediente judicial (CSS) es obligatorio.");
            return;
        }
        if (!datosApelacion.juzgado || datosApelacion.juzgado.trim() === '') {
            toast.error("El juzgado es obligatorio.");
            return;
        }
        if (!datosApelacion.fechaPresentacion) {
            toast.error("La fecha de presentación es obligatoria.");
            return;
        }
        const archivosPendientes = archivos.filter(a => a.estado === 'pendiente');
        if (archivosPendientes.length === 0) {
            toast.error("Debe adjuntar el escrito de demanda.");
            return;
        }
        setCargando(true);
        setSubiendoArchivos(true);
        try {
            // Subir archivos
            const archivosSubidos = [];
            for (const archivo of archivosPendientes) {
                try {
                    const archivoSubido = await subirArchivo(tramite.idTramite, archivo.file);
                    archivosSubidos.push(archivoSubido);
                    setArchivos(prev => prev.map(item =>
                        item.id === archivo.id ? { ...item, estado: "subido", idArchivo: archivoSubido.idArchivo, esDelBackend: true } : item
                    ));
                } catch (error) {
                    toast.error(`Error subiendo ${archivo.nombre}`);
                    throw error;
                }
            }
            setSubiendoArchivos(false);

            // Eventos en el calendario:
            // - El evento de vencimiento se gestionó dinámicamente con sincronizarEventoVencimiento
            // - Al registrar la apelación, eliminamos ese evento de vencimiento pendientes
            // - No creamos eventos históricos de presentación a pedido del usuario.

            // Crear comentario
            const comentario = `Segunda Apelación (Demanda Judicial) registrada\nFecha de notificación del dictamen CMC: ${formatearFecha(datosApelacion.fechaNotificacion)}\nPorcentaje de incapacidad: ${datosApelacion.porcentaje}%\nFecha límite para apelar: ${formatearFecha(datosApelacion.fechaLimite)}\nNúmero de expediente judicial (CSS): ${datosApelacion.numeroExpedienteJudicial}\nJuzgado: ${datosApelacion.juzgado}\nSala: ${datosApelacion.sala || 'No especificada'}\nFecha de presentación: ${formatearFecha(datosApelacion.fechaPresentacion)}\nArchivos adjuntos: ${archivosSubidos.map(a => a.nombre).join(', ')}`;

            // Eliminar evento de vencimiento automático ya que se cumplió la tarea
            await eliminarEventoVencimiento("2° Apelación");

            // Cambiar a estado "Trámite en Juicio" (estado 11 - Instancia Judicial)
            const cambioEstadoData = {
                idEstado: 11, // Instancia Judicial / Trámite en Juicio
                comentario: comentario
            };

            try {
                await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);
            } catch (error) {
                console.error("Error al cambiar estado:", error);
                throw error;
            }

            toast.success("Segunda Apelación (Demanda Judicial) registrada correctamente. El trámite ahora está en Juicio.");

            // Limpiar formulario
            setMostrarFormularioApelacion(false);
            setArchivos([]);
            setDatosApelacion({
                fechaNotificacion: '',
                porcentaje: '',
                fechaLimite: '',
                numeroExpedienteJudicial: '',
                juzgado: '',
                sala: '',
                fechaPresentacion: '',
                archivoDemanda: null
            });

            if (onEstadoCambiado) {
                await onEstadoCambiado();
            }

        } catch (error) {
            console.error("Error al registrar segunda apelación:", error);
            toast.error("Error al registrar la segunda apelación: " + (error.response?.data || error.message));
        } finally {
            setCargando(false);
            setSubiendoArchivos(false);
        }
    };

    const formatearFecha = (fechaString) => {
        if (!fechaString) return '';
        const fecha = new Date(fechaString);
        if (isNaN(fecha.getTime())) return '';
        const fechaLocal = new Date(fecha.valueOf() + fecha.getTimezoneOffset() * 60000);
        return fechaLocal.toLocaleDateString('es-ES', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const obtenerClaseColorContador = () => {
        if (vencido) return 'bg-secondary text-white';
        if (diasRestantes === 0) return 'bg-danger text-white';
        if (diasRestantes <= 2) return 'bg-warning text-dark';
        return 'bg-warning text-dark';
    };

    const obtenerTextoContador = () => {
        if (vencido) return 'VENCIDO';
        if (diasRestantes === 0) return 'ÚLTIMO DÍA';
        if (diasRestantes === 1) return '1 día hábil restante';
        return `${diasRestantes} días hábiles restantes`;
    };

    if (cargandoHistorial) {
        return (
            <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
                <div className="alert alert-info">Cargando información del dictamen CMC...</div>
            </div>
        );
    }

    return (
        <div className="card shadow-lg border-0 rounded-4 p-4 mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4 className="fw-bold text-warning mb-0">
                    <GavelIcon className="me-2" />
                    2° Apelación (Demanda Judicial)
                </h4>
                <span className={`badge ${obtenerClaseColorContador()} rounded-pill px-3 py-2`}>
                    <AccessTimeIcon fontSize="small" className="me-1" />
                    {diasRestantes !== null ? obtenerTextoContador() : 'Calculando...'}
                </span>
            </div>

            {vencido && (
                <div className="alert alert-danger border-0 rounded-3">
                    <WarningIcon className="me-2" />
                    <strong>Plazo Vencido:</strong> El plazo para presentar la segunda apelación (demanda judicial) ha vencido.
                </div>
            )}

            {!vencido && diasRestantes !== null && diasRestantes <= 2 && diasRestantes >= 0 && (
                <div className={`alert alert-${colorAlerta} border-0 rounded-3`}>
                    <WarningIcon className="me-2" />
                    <strong>ALERTA - Plazo por Vencer:</strong> Tienes {diasRestantes === 0 ? 'hoy (ÚLTIMO DÍA)' : `${diasRestantes} día(s) hábiles`} para presentar la segunda apelación (demanda judicial).
                    <br />
                    <small className="mt-2 d-block">Por favor, registre la demanda lo antes posible para evitar el cierre automático del trámite.</small>
                </div>
            )}
            {!vencido && diasRestantes !== null && diasRestantes > 2 && (
                <div className="alert alert-info border-0 rounded-3">
                    <strong>Plazo para Presentar Demanda:</strong> Tienes {diasRestantes} día(s) hábiles para presentar la segunda apelación (demanda judicial).
                </div>
            )}

            {/* Descripción del Estado */}
            <div className="alert alert-light border border-warning rounded-3 mb-3">
                <h6 className="fw-bold text-warning mb-2">
                    <GavelIcon className="me-2" fontSize="small" />
                    ¿Qué significa la 2° Apelación?
                </h6>
                <div className="text-muted" style={{ fontSize: '0.95rem', lineHeight: '1.6' }}>
                    <p className="mb-2">
                        <strong>La segunda apelación solo ocurre si el dictamen de la CMC continúa siendo menor al 66%.</strong>
                    </p>
                    <ul className="mb-2 ps-3">
                        <li>Si el dictamen CMC otorga <strong>≥ 66%</strong>: El trámite se completa automáticamente y <strong>NO</strong> hay segunda apelación.</li>
                        <li>Si el dictamen CMC otorga <strong>&lt; 66%</strong>: Se inicia la segunda apelación (demanda judicial) y el trámite pasa a la etapa judicial.</li>
                    </ul>
                    <p className="mb-0">
                        <strong>Plazo:</strong> Se dispone de <strong>5 días hábiles</strong> desde la notificación del dictamen CMC para presentar la demanda judicial ante el poder judicial.
                        Este es el comienzo de la etapa judicial del trámite.
                    </p>
                </div>
            </div>

            {/* Información del Dictamen CMC */}
            {infoDictamenCMC && (
                <div className="mt-3 p-3 bg-light rounded-3 border">
                    <h6 className="fw-semibold text-secondary mb-3">Información del Dictamen CMC</h6>

                    {/* Alerta si el porcentaje es >= 66% (esto no debería pasar) */}
                    {infoDictamenCMC.porcentaje !== null && infoDictamenCMC.porcentaje >= 66 && (
                        <div className="alert alert-danger border-0 rounded-3 mb-3">
                            <WarningIcon className="me-2" />
                            <strong>⚠️ ERROR DE ESTADO:</strong> El dictamen CMC otorga {infoDictamenCMC.porcentaje}% (≥ 66%),
                            por lo que el trámite debería haberse completado automáticamente y NO debería estar en Segunda Apelación.
                            Por favor, contacte al administrador del sistema.
                        </div>
                    )}

                    <div className="row g-3">
                        {infoDictamenCMC.porcentaje !== null && (
                            <div className="col-md-4 d-flex align-items-center">
                                <PercentIcon className={`me-2 ${infoDictamenCMC.porcentaje < 66 ? 'text-primary' : 'text-danger'}`} />
                                <div>
                                    <small className="text-muted d-block">Porcentaje de Incapacidad</small>
                                    <strong className={`text-dark ${infoDictamenCMC.porcentaje >= 66 ? 'text-danger' : ''}`}>
                                        {infoDictamenCMC.porcentaje}% {infoDictamenCMC.porcentaje < 66 ? '(Rechazado)' : '(Aprobado - ERROR)'}
                                    </strong>
                                </div>
                            </div>
                        )}
                        {infoDictamenCMC.resultado && (
                            <div className="col-md-4 d-flex align-items-center">
                                <CheckCircleIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Resultado</small>
                                    <strong className="text-dark">{infoDictamenCMC.resultado}</strong>
                                </div>
                            </div>
                        )}
                        {infoDictamenCMC.fechaDictamen && (
                            <div className="col-md-4 d-flex align-items-center">
                                <CalendarMonthIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Fecha del Dictamen CMC</small>
                                    <strong className="text-dark">{infoDictamenCMC.fechaDictamen}</strong>
                                </div>
                            </div>
                        )}
                        {infoDictamenCMC.fechaLimite && (
                            <div className="col-md-4 d-flex align-items-center">
                                <AccessTimeIcon className="text-primary me-2" />
                                <div>
                                    <small className="text-muted d-block">Fecha Límite para Apelar</small>
                                    <strong className="text-dark">{infoDictamenCMC.fechaLimite}</strong>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {mostrarFormularioApelacion ? (
                <div className="mt-4">
                    <div className="card shadow-sm border-0 rounded-4 p-4">
                        <h5 className="fw-semibold text-primary mb-3">Registrar Segunda Apelación (Demanda Judicial)</h5>

                        <div className="row g-3">
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Fecha de Notificación del Dictamen CMC <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={datosApelacion.fechaNotificacion}
                                    onChange={e => handleFechaNotificacionChange(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Porcentaje de Incapacidad (%) <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min="0"
                                    max="100"
                                    step="0.01"
                                    value={datosApelacion.porcentaje}
                                    onChange={e => setDatosApelacion({ ...datosApelacion, porcentaje: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-md-4">
                                <label className="form-label fw-semibold">
                                    Fecha Límite para Apelar <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={datosApelacion.fechaLimite}
                                    onChange={e => setDatosApelacion({ ...datosApelacion, fechaLimite: e.target.value })}
                                    required
                                    readOnly
                                />
                                <small className="text-muted">Calculada automáticamente (5 días hábiles)</small>
                            </div>

                            <div className="col-12">
                                <hr className="my-3" />
                                <h6 className="fw-semibold text-secondary mb-3">Datos Judiciales</h6>
                            </div>

                            <div className="col-md-6">
                                <label className="form-label fw-semibold">
                                    Número de Expediente Judicial (CSS) <span className="text-danger">*</span>
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light">
                                        <DescriptionIcon fontSize="small" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={datosApelacion.numeroExpedienteJudicial}
                                        onChange={e => setDatosApelacion({ ...datosApelacion, numeroExpedienteJudicial: e.target.value })}
                                        placeholder="Ej: CSS-2024-001234"
                                        required
                                    />
                                </div>
                                <small className="text-muted">Número de expediente asignado por el poder judicial</small>
                            </div>
                            <div className="col-md-6">
                                <ComboboxDatosMaestros
                                    opciones={lugares.filter(l => l.tipo === 'Juzgado' || !l.tipo)}
                                    valor={datosApelacion.juzgado}
                                    onChange={(valor) => setDatosApelacion({ ...datosApelacion, juzgado: valor })}
                                    tipo="lugar"
                                    tipoLugar="Juzgado"
                                    placeholder="Seleccione o escriba un juzgado..."
                                    label="Juzgado"
                                    required={true}
                                    icon={BusinessIcon}
                                />
                            </div>
                            <div className="col-md-6">
                                <ComboboxDatosMaestros
                                    opciones={lugares.filter(l => l.tipo === 'Sala' || l.tipo === 'Dependencia' || !l.tipo)}
                                    valor={datosApelacion.sala}
                                    onChange={(valor) => setDatosApelacion({ ...datosApelacion, sala: valor })}
                                    tipo="lugar"
                                    tipoLugar="Sala"
                                    placeholder="Seleccione o escriba una sala..."
                                    label="Sala"
                                    icon={BusinessIcon}
                                />
                            </div>
                            <div className="col-md-6">
                                <label className="form-label fw-semibold">
                                    Fecha de Presentación <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={datosApelacion.fechaPresentacion}
                                    onChange={e => setDatosApelacion({ ...datosApelacion, fechaPresentacion: e.target.value })}
                                    required
                                />
                            </div>

                        </div>

                        {/* Gestor de Archivos */}
                        <div className="mt-4">
                            <h6 className="fw-semibold text-primary">Archivo de Apelación (PDF) <span className="text-danger">*</span></h6>
                            {archivos.length > 0 && (
                                <div className="list-group mb-3">
                                    {archivos.map(archivo => (
                                        <div key={archivo.id} className="list-group-item d-flex justify-content-between align-items-center">
                                            <div>
                                                <AttachFileIcon fontSize="small" className="me-2 text-muted" />
                                                <span className="fw-semibold">{archivo.nombre}</span>
                                                <small className="text-muted ms-2">({archivo.tamaño})</small>
                                                <span className={`badge ms-2 ${archivo.estado === 'subido' ? 'bg-success' : 'bg-warning text-dark'}`}>
                                                    {archivo.estado}
                                                </span>
                                            </div>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => handleEliminarArchivo(archivo.id)}
                                                title="Eliminar"
                                            >
                                                <DeleteIcon fontSize="small" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div
                                className={`border rounded-3 p-4 text-center cursor-pointer ${dragActive ? "border-primary bg-primary bg-opacity-10" : "border-dashed"}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleSeleccionarArchivos}
                                    className="d-none"
                                    accept=".pdf,.PDF"
                                />
                                <CloudUploadIcon className="text-muted mb-2" />
                                <p className="mb-0">Arrastra el archivo PDF aquí o haz clic para seleccionar</p>
                                <small className="text-muted">Solo archivos PDF</small>
                            </div>
                        </div>

                        <div className="d-flex justify-content-center gap-3 mt-4">
                            <button
                                className="btn btn-success d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={handleRegistrarSegundaApelacion}
                                disabled={cargando || subiendoArchivos || vencido}
                            >
                                <CheckCircleIcon fontSize="small" />
                                {cargando || subiendoArchivos ? 'Registrando...' : 'Registrar Segunda Apelación'}
                            </button>
                            <button
                                className="btn btn-outline-secondary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={() => setMostrarFormularioApelacion(false)}
                                disabled={cargando || subiendoArchivos}
                            >
                                <CancelIcon fontSize="small" />
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="d-flex justify-content-center flex-wrap gap-3 mt-4 pt-3 border-top">
                    <button
                        className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                        onClick={() => setMostrarFormularioApelacion(true)}
                        disabled={cargando || vencido}
                    >
                        <UploadFileIcon fontSize="small" />
                        Registrar Segunda Apelación
                    </button>
                    {vencido && (
                        <>
                            <button
                                className="btn btn-danger d-flex align-items-center gap-2 rounded-pill px-4 py-2 fw-semibold"
                                onClick={() => setMostrarModalCerrarTramite(true)}
                                disabled={cargando}
                            >
                                <CancelIcon fontSize="small" />
                                Cerrar Trámite (Vencido)
                            </button>
                            <ModalConfirmacionCambioEstado
                                isOpen={mostrarModalCerrarTramite}
                                onClose={() => setMostrarModalCerrarTramite(false)}
                                onConfirm={async () => {
                                    setCargando(true);
                                    try {
                                        const cambioEstadoData = {
                                            idEstado: 13, // Rechazado
                                            comentario: 'Trámite cerrado automáticamente por vencimiento del plazo para presentar la segunda apelación (5 días hábiles).'
                                        };
                                        await cambioEstadoService.cambiarEstado(tramite.idTramite, cambioEstadoData);
                                        toast.success("Trámite cerrado por vencimiento del plazo.");
                                        setMostrarModalCerrarTramite(false);
                                        if (onEstadoCambiado) await onEstadoCambiado();
                                    } catch (error) {
                                        console.error("Error al cerrar trámite:", error);
                                        toast.error("Error al cerrar el trámite.");
                                    } finally {
                                        setCargando(false);
                                    }
                                }}
                                titulo="Confirmar Cierre de Trámite"
                                mensaje="¿Confirma que desea cerrar el trámite por vencimiento del plazo para presentar la segunda apelación?"
                                siguienteEstado="Rechazado"
                                cargando={cargando}
                                textoBotonConfirmar="Cerrar Trámite"
                            />
                        </>
                    )}
                </div>
            )}
        </div>
    );
}

export default GestorEstadoSegundaApelacion;
